// src/app/pages/shared/TenantSelectorPage.jsx
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { ThemeLogo } from 'components/theme';
import { useAuthContext } from 'app/contexts/auth/context';
import { useAdminTenantContext } from 'app/contexts/adminTenant/context';
import {
  useAdminSelectTenantMutation,
  useAdminTenantsQuery,
} from 'api/queries/useAdminTenantQueries';
import { APP_MODULE } from 'constants/module.constant';
import { doLogout } from 'utils/keycloak';
import { isUserAdmin } from 'hooks/useIsAdmin';
import AdminDashboardCard from './onBoarding/AdminDashboardCard';

// ----------------------------------------------------------------------

const isAdminUser = (user) => isUserAdmin(user);

// ----------------------------------------------------------------------

const getInitials = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';

const ModuleBadge = ({ children, active = false }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${
      active
        ? 'bg-primary-50 text-primary-700 dark:bg-primary-500/10 dark:text-primary-300'
        : 'bg-gray-100 text-gray-500 dark:bg-dark-700 dark:text-dark-300'
    }`}
  >
    {children}
  </span>
);

ModuleBadge.propTypes = {
  children: PropTypes.node,
  active: PropTypes.bool,
};

// ----------------------------------------------------------------------

export default function TenantSelectorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthContext();
  const { selectTenant } = useAdminTenantContext();

  const [pendingTenantId, setPendingTenantId] = useState(null);

  const userIsAdmin = isAdminUser(user);

  // Reflect the active app module in the URL (?module=monitoring) so the
  // tenant picker URL is self-describing and shareable.
  useEffect(() => {
    if (!userIsAdmin) return;
    const moduleParam = APP_MODULE.toLowerCase();
    if (searchParams.get('module') !== moduleParam) {
      const next = new URLSearchParams(searchParams);
      next.set('module', moduleParam);
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, userIsAdmin]);

  const { data: tenants, isLoading, isError } = useAdminTenantsQuery({
    enabled: userIsAdmin,
  });

  const selectMutation = useAdminSelectTenantMutation({
    onSuccess: (_data, tenantId) => {
      const tenant = tenants?.find((item) => item.id === tenantId);
      if (tenant) selectTenant(tenant);
      navigate('/', { replace: true });
    },
    onError: () => setPendingTenantId(null),
  });

  const handleSelect = (tenant) => {
    setPendingTenantId(tenant.id);
    selectMutation.mutate(tenant.id);
  };

  const greetingName = useMemo(
    () =>
      user?.firstName ||
      user?.first_name ||
      user?.name ||
      user?.username ||
      '',
    [user]
  );

  // Hard guard: only administrators may use the tenant picker. Non-admin
  // users always work within their own tenant (set from the JWT claim).
  if (!userIsAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="fixed inset-0 overflow-y-auto bg-gray-50 dark:bg-dark-900">
      <div className="mx-auto flex min-h-full w-full max-w-4xl flex-col px-4 py-10 sm:px-6">
        <header className="flex items-center justify-between">
          <ThemeLogo size="medium" className="size-18" />
          <button
            type="button"
            onClick={doLogout}
            className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-dark-300 dark:hover:text-dark-100"
          >
            {t('adminTenantSelector.signOut')}
          </button>
        </header>

        <section className="mt-12 text-center">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-dark-100 sm:text-4xl">
            {t('adminTenantSelector.greeting', { name: greetingName })}
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-base text-gray-600 dark:text-dark-300 sm:text-lg">
            {t('adminTenantSelector.subtitle')}
          </p>
        </section>

        <section className="mt-8">
          {isLoading ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-20 animate-pulse rounded-lg border border-gray-200 bg-white dark:border-dark-600 dark:bg-dark-800"
                />
              ))}
            </div>
          ) : isError ? (
            <div className="rounded-lg border border-error/30 bg-error/10 p-4 text-center text-sm text-error">
              {t('adminTenantSelector.loadError')}
            </div>
          ) : !tenants || tenants.length === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-600 dark:border-dark-600 dark:bg-dark-800 dark:text-dark-300">
              {t('adminTenantSelector.noTenants')}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {tenants.map((tenant) => {
                const isPending =
                  selectMutation.isLoading && pendingTenantId === tenant.id;
                const isOtherPending =
                  selectMutation.isLoading && pendingTenantId !== tenant.id;
                const modules = Array.isArray(tenant.modules)
                  ? tenant.modules
                  : [];

                return (
                  <button
                    key={tenant.id}
                    type="button"
                    disabled={selectMutation.isLoading}
                    onClick={() => handleSelect(tenant)}
                    className={`group flex items-center gap-3 rounded-lg border bg-white p-4 text-left transition dark:bg-dark-800
                      ${
                        isPending
                          ? 'border-primary-500 ring-2 ring-primary-500/30'
                          : 'border-gray-200 hover:border-primary-400 hover:shadow-sm dark:border-dark-600 dark:hover:border-primary-400'
                      }
                      ${isOtherPending ? 'opacity-50' : ''}
                      disabled:cursor-not-allowed`}
                  >
                    <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-white p-1.5 dark:border-dark-600 dark:bg-dark-700">
                      {tenant.logo ? (
                        <img
                          src={tenant.logo}
                          alt=""
                          className="max-h-full max-w-full object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <span className="text-sm font-bold text-primary-700 dark:text-primary-300">
                          {getInitials(tenant.name)}
                        </span>
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-sm font-semibold text-gray-800 dark:text-dark-100">
                        {tenant.name}
                      </h3>
                      {modules.length > 0 ? (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {modules.map((mod) => (
                            <ModuleBadge
                              key={mod.module}
                              active={
                                String(mod.module).toLowerCase() ===
                                APP_MODULE.toLowerCase()
                              }
                            >
                              {mod.module}
                            </ModuleBadge>
                          ))}
                        </div>
                      ) : null}
                    </div>

                    {isPending ? (
                      <span className="size-4 shrink-0 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="size-4 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-primary-600 dark:group-hover:text-primary-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <AdminDashboardCard href={import.meta.env.VITE_REACT_ADMIN_ROUTE} />
      </div>
    </div>
  );
}
